import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useRef } from "react";
import CustomAddressAutocomplete from "../../components/CustomAddressAutocomplete";
import './AddressAndCoordinates.css';

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

// Используем forwardRef, чтобы передать реф от родителя
const AddressAndCoordinates = forwardRef(
  (
    {
      addressAndCoordinates,
      setAddressAndCoordinates,
      setSelfPickUp,
      dealId,
      locationFromDeal,
      selectedRegion,
      addressValidationError,
      setAddressValidationError
    },
    ref
  ) => {
    const [value, setValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [isFromDeal, setIsFromDeal] = useState(false);
    const inputRef = useRef(null);

    // При монтировании компонента автоматически подтягиваем адрес из сделки
    useEffect(() => {
      if (dealId && !isNaN(Number(dealId)) && locationFromDeal && locationFromDeal.address && locationFromDeal.coordinates) {
        // Создаем объект suggestion для отображения в input
        const suggestionFromDeal = {
          value: locationFromDeal.address,
          data: {
            geo_lat: locationFromDeal.coordinates.latitude,
            geo_lon: locationFromDeal.coordinates.longitude
          }
        };

        setAddressAndCoordinates({
          address: locationFromDeal.address,
          coordinates: {
            latitude: parseFloat(locationFromDeal.coordinates.latitude),
            longitude: parseFloat(locationFromDeal.coordinates.longitude),
          },
        });

        // Записываем адрес в input
        setValue(suggestionFromDeal);
        setIsFromDeal(true);
        setSuccess(true);
        setError(null);

        console.log("✅ Адрес подтянут из сделки:", locationFromDeal.address);
      }
    }, [dealId, locationFromDeal, setAddressAndCoordinates]);

    const handleAddressSelect = (suggestion) => {
      if (suggestion) {
        const { data } = suggestion;
        console.log(suggestion);
        setValue(suggestion);
        setIsFromDeal(false); // Пользователь выбрал адрес вручную
        setAddressAndCoordinates({
          address: suggestion.value,
          coordinates: {
            latitude: parseFloat(data.geo_lat),
            longitude: parseFloat(data.geo_lon),
          }
        });
        setSuccess(true);
        setError(null);
        if (setAddressValidationError) setAddressValidationError(null);
      }
    };

    const handleReset = () => {
      setValue(null);
      setIsFromDeal(false);
      setAddressAndCoordinates({
        address: "Санкт-Петербург",
        coordinates: {
          latitude: 59.9342802,
          longitude: 30.3350986
        }
      });
      setSuccess(false);
      setError(null);
      if (setAddressValidationError) setAddressValidationError(null);
    };

    useImperativeHandle(ref, () => ({
      convertData: () => {
        if (!value) {
          setError("Введите корректные данные");
          setSuccess(false);
          return;
        }

        setLoading(true);
        setError(null);

        fetch(`${PATH_TO_BACKEND}/api/getLocation`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: value.value }),
        })
          .then((response) => response.json())
          .then((data) => {
            console.log(data);
            if (data.address && data.coordinates) {
              const lat = parseFloat(data.coordinates.latitude);
              const lon = parseFloat(data.coordinates.longitude);
              if (!isNaN(lat) && !isNaN(lon)) { // Дополнительная проверка на NaN
                setAddressAndCoordinates({
                  address: data.address,
                  coordinates: {
                    latitude: lat,
                    longitude: lon,
                  },
                });
                setSuccess(true);
                setError(null);
              } else {
                setError("Не удалось получить координаты с сервера");
                setSuccess(false);
              }
            } else {
              setError("Не удалось преобразовать данные");
              setSuccess(false);
            }
          })
          .catch((error) => {
            console.error("Ошибка запроса:", error);
            setError("Ошибка получения данных");
            setSuccess(false);
          })
          .finally(() => setLoading(false));
      },
    }));

    const handlePullFromDeal = () => {
      if (dealId && !isNaN(Number(dealId)) && locationFromDeal && locationFromDeal.address && locationFromDeal.coordinates) {
        // Создаем объект suggestion для отображения в input
        const suggestionFromDeal = {
          value: locationFromDeal.address,
          data: {
            geo_lat: locationFromDeal.coordinates.latitude,
            geo_lon: locationFromDeal.coordinates.longitude
          }
        };

        setAddressAndCoordinates({
          address: locationFromDeal.address,
          coordinates: {
            latitude: parseFloat(locationFromDeal.coordinates.latitude),
            longitude: parseFloat(locationFromDeal.coordinates.longitude),
          },
        });

        // Записываем адрес в input
        setValue(suggestionFromDeal);
        setIsFromDeal(true);
        setSuccess(true);
        setError(null);

        console.log("✅ Адрес подтянут из сделки вручную:", locationFromDeal.address);
      }
    };

    // Сбрасываем "Адрес успешно найден", когда пользователь делает новый ввод 
    const handleInputChange = (inputValue) => {
      setSuccess(false);
      setError(null)
    }

    const showDealPullBtn = dealId && !isNaN(Number(dealId)) && locationFromDeal && locationFromDeal.address;

    return (
      <div className="address-block">
        <div className="address-block__input-row">
          <div className="address-block__input-row-inner">
            <div className="address-block__input-grow address-block__input-grow--with-pin">
              <span className="address-block__pin" aria-hidden="true" />
              <CustomAddressAutocomplete
                token={process.env.REACT_APP_DADATA_TOKEN}
                value={value}
                onChange={handleAddressSelect}
                placeholder="Введите адрес или координаты..."
                filterLocations={[]}
                selectedRegion={selectedRegion}
                setAddressValidationError={setAddressValidationError}
                onInputChange={handleInputChange}
              />
            </div>
            {showDealPullBtn && (
              <button
                type="button"
                className="address-block__deal-button"
                onClick={handlePullFromDeal}
                title="Подтянуть адрес из сделки"
                aria-label="Подтянуть адрес из сделки"
              >
                🔃
              </button>
            )}
            {value && (
              <button
                type="button"
                className="address-block__reset-button"
                onClick={handleReset}
                title="Сбросить адрес"
                aria-label="Сбросить адрес"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Статус адреса. Приоритет: addressValidationError → error → success */}
        {addressValidationError && (
          <div className="address-block__status address-block__status--error">
            {addressValidationError}
          </div>
        )}
        {!addressValidationError && error && (
          <p className="address-block__status address-block__status--error">{error}</p>
        )}
        {!addressValidationError && !error && isFromDeal && success && (
          <div className="address-block__status address-block__status--success">
            Адрес подтянут из сделки
          </div>
        )}
        {!addressValidationError && !error && success && !isFromDeal && (
          <p className="address-block__status address-block__status--success">Адрес успешно найден</p>
        )}
      </div>
    );
  }
);

export default AddressAndCoordinates;
